import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { getLead, patchLead } from '../../../Redux/util/Apis/leads';
import CopyIcon from '../../../Media/InboxV1/CpoyIcon';
import EditIcon from '../../../Media/InboxV1/EditIcon';
import GreenTick from '../../../Media/InboxV1/GreenTick';
import InfoIcon from '../../../Media/InboxV1/InfoIcon';
import WrongPhone from '../../../Media/InboxV1/WrongPhone';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from 'styled-components';

const ContactDetailsPopup = ({
  open,
  setOpen,
  name,
  smsEnabled,
  phoneType,
  selectedLeadId,
  refetchLeadInfo
}) => {
  const theme = useTheme();
  const [editable, setEditable] = useState(false);
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [updatedMetaData, setUpdatedMetaData] = useState({ name });
  const [snackBarMessage, setSnackbarMessage] = useState({});
  const [isSuccess, setIsSuccess] = useState(false);
  const [sensitiveData, setSensitiveData] = useState({});

  const handleChange = e => {
    setUpdatedMetaData({ ...updatedMetaData, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    if (sensitiveData && Object.keys(sensitiveData)?.length) {
      setUpdatedMetaData({ ...sensitiveData, name });
    }
  }, [sensitiveData]);

  useEffect(() => {
    if (!open || !selectedLeadId) return;
    let cancelled = false;
    (async () => {
      const result = await getLead(selectedLeadId, { sensitive: true });
      if (cancelled) return;
      if (result?.error) {
        setSnackbarMessage({ message: result.message, type: 'error' });
        return;
      }
      setSensitiveData(result || {});
    })();
    return () => {
      cancelled = true;
    };
  }, [open, selectedLeadId]);

  const saveNewLeadDetails = async () => {
    const { name, email, phone } = updatedMetaData;
    const result = await patchLead(selectedLeadId, { name, email, phone });
    if (result?.error) {
      setSnackbarMessage({ message: result.message, type: 'error' });
      setIsSuccess(false);
      return;
    }
    setIsSuccess(true);
    refetchLeadInfo?.();
  };

  const handleClose = () => {
    setOpen(false);
    setEditable(false);
  };

  return (
    <>
      {isSuccess && (
        <Snackbar
          open={snackBarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackBarOpen(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert severity={snackBarMessage.type}>
            {snackBarMessage.message}
          </Alert>
        </Snackbar>
      )}

      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: { width: 380, maxWidth: '90vw' }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontWeight: 500,
            color: theme.text.heading,
            fontSize: '16px',
            pb: 1
          }}
        >
          Personal Details
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {!editable && (
              <IconButton size="small" onClick={() => setEditable(true)}>
                <EditIcon
                  style={{ width: 20, height: 20, color: theme.text.heading }}
                />
              </IconButton>
            )}
            <IconButton size="small" onClick={handleClose}>
              <CloseIcon sx={{ fontSize: 20, color: theme.text.heading }} />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          {editable ? (
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}
            >
              <TextField
                sx={{ input: { color: theme.text.value, fontSize: '14px' } }}
                placeholder="Name"
                name="name"
                label="Name"
                variant="outlined"
                size="small"
                fullWidth
                value={updatedMetaData?.name || ''}
                onChange={handleChange}
              />
              <TextField
                sx={{ input: { color: theme.text.value, fontSize: '14px' } }}
                placeholder="Phone"
                name="phone"
                label="Phone"
                variant="outlined"
                size="small"
                fullWidth
                value={updatedMetaData?.phone || ''}
                onChange={handleChange}
              />
              <TextField
                sx={{ input: { color: theme.text.value, fontSize: '14px' } }}
                placeholder="E-mail"
                name="email"
                label="E-mail"
                variant="outlined"
                size="small"
                fullWidth
                value={updatedMetaData?.email || ''}
                onChange={handleChange}
              />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  sx={{
                    color: theme.text.heading,
                    fontWeight: 500,
                    fontSize: '14px'
                  }}
                >
                  Name:
                </Typography>
                <Typography
                  sx={{
                    color: theme.text.value,
                    fontWeight: 400,
                    fontSize: '14px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {name || ''}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  sx={{
                    color: theme.text.heading,
                    fontWeight: 500,
                    fontSize: '14px'
                  }}
                >
                  Phone:
                </Typography>
                <Typography
                  sx={{
                    color: theme.text.value,
                    fontWeight: 400,
                    fontSize: '14px'
                  }}
                >
                  {updatedMetaData?.phone || ''}
                </Typography>
                {smsEnabled ? (
                  <GreenTick style={{ width: 18, height: 18 }} />
                ) : (
                  <WrongPhone style={{ width: 18, height: 18 }} />
                )}
                <Tooltip
                  enterDelay={500}
                  leaveDelay={200}
                  title={'Phone-Type: ' + (phoneType || 'Not-available')}
                >
                  <IconButton sx={{ p: 0 }}>
                    <InfoIcon
                      style={{
                        width: 20,
                        height: 20,
                        color: theme.text.heading
                      }}
                    />
                  </IconButton>
                </Tooltip>
                <IconButton
                  sx={{ p: 0 }}
                  onClick={() =>
                    navigator.clipboard.writeText(updatedMetaData?.phone || '')
                  }
                >
                  <CopyIcon
                    style={{ width: 18, height: 18, color: theme.text.heading }}
                  />
                </IconButton>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  sx={{
                    color: theme.text.heading,
                    fontWeight: 500,
                    fontSize: '14px'
                  }}
                >
                  Email:
                </Typography>
                <Typography
                  sx={{
                    color: theme.text.value,
                    fontWeight: 400,
                    fontSize: '14px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {updatedMetaData?.email || ''}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>

        {editable && (
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button variant="text" onClick={() => setEditable(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                saveNewLeadDetails({ ...updatedMetaData }, true);
                setOpen(false);
                setSnackBarOpen(true);
                setEditable(false);
              }}
            >
              Save
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
};

export default ContactDetailsPopup;
